import { useContext, useEffect, useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import type {
  ServiceArgs,
  ServiceEventsNames,
  ServiceInstance,
  ServicesNames,
} from '@mint-lib/service-locator';

import { MintServiceLocator } from '../providers/MintServiceLocator.js';
import type { MintServices } from '../types.js';

export type ServiceConfig<
  Key extends ServicesNames<MintServices> = ServicesNames<MintServices>,
  Args extends ServiceArgs<MintServices, Key> = ServiceArgs<MintServices, Key>,
> = {
  /**
   * The name of the service to use
   */
  name: Key;
  /**
   * Subscribe to service events that should trigger a refresh of the instance
   * @default ['destroy']
   */
  watch?: Exclude<
    ServiceEventsNames<MintServices, Key, Args>,
    'destroy' | 'create'
  >[];
  /**
   * Fail if the service is not found
   * @default false
   */
  failOnNotFound?: boolean;
  /**
   * If true, the service will be fetched in a suspense mode
   * Please be very careful with this option, as it can lead to resetting the whole component tree
   */
  suspense?: true;
} & (Args extends []
  ? {}
  : {
      /**
       * Arguments to pass to the service creation
       */
      args: Args;
    });

/**
 * Hook to get a service instance from the service locator
 *
 * @param config The configuration of the service to use
 * @returns A tuple with the instance and the version of the instance
 * @example
 * const [authService, version] = useService({
 *   name: 'auth',
 *   failOnNotFound: true,
 * });
 *
 * authService.isLoggedIn();
 *
 * @example
 * const [profileService, version] = useService({
 *  name: 'profile',
 *  failOnNotFound: true,
 *  watch: ['update'],
 * });
 *
 * // Will be updated when the profile is updated
 * console.log(profileService.getProfileData());
 */
export function useService<
  Config extends ServiceConfig<Key, Args>,
  Key extends ServicesNames<MintServices> = Config['name'],
  // @ts-expect-error TS2344 Args is correctly inferred
  Args extends ServiceArgs<MintServices, Key> = Config['args'],
  Service extends ServiceInstance<MintServices, Key, Args> = ServiceInstance<
    MintServices,
    Key,
    Args
  >,
>({
  name,
  watch,
  failOnNotFound,
  suspense,
  // @ts-expect-error TS2339 args is required only if Args is not []
  args = [],
}: Config): [
  Config['failOnNotFound'] extends true
    ? Service
    : Config['suspense'] extends true
      ? Service
      : Service | null,
  number,
] {
  const serviceLocator = useContext(MintServiceLocator);
  if (!serviceLocator) {
    throw new Error('No RouterContext found');
  }
  const [instance, setInstance] = useState<Service | null>(
    // Let's try to get the instance synchronously first
    () => serviceLocator.getSyncInstance<Key, Args, Service>(name, ...args),
  );
  const [version, setVersion] = useState(0);
  if (suspense && !instance) {
    throw serviceLocator.getOrThrowInstance<Key, Args, Service>(name, ...args);
  }
  if (!instance && failOnNotFound) {
    throw new Error('Instance not found in the service locator');
  }

  const refresh = useEvent((unmounted: { current: boolean }) => {
    serviceLocator
      .getOrThrowInstance<Key, Args, Service>(name, ...args)
      .then((inst) => {
        if (unmounted.current) {
          return;
        }
        setInstance(inst);
        setVersion((v) => v + 1);
      })
      .catch((e) => {
        console.error(e);
      });
  });

  useEffect(() => {
    if (instance || failOnNotFound) {
      return;
    }
    const unmounted = { current: false };
    refresh(unmounted);
    return () => {
      unmounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failOnNotFound, instance, name, ...args]);

  // Subscribe to changes in the service locator
  useEffect(() => {
    const unmounted = { current: false };
    const handleEvent = () => requestAnimationFrame(() => refresh(unmounted));
    const instanceName = serviceLocator.makeInstanceName(name, args);
    const subscriptions = [...new Set([...(watch ?? []), 'destroy'])].map(
      (event) =>
        serviceLocator.getEventBus().on(instanceName, event, handleEvent),
    );
    return () => {
      unmounted.current = true;
      subscriptions.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, ...(watch ?? []), ...args]);

  // @ts-expect-error TS2322. We know that instance is not null if failOnNotFound is true
  return [instance, version];
}
